/**
 * Phase 291 — Communication Settings (clean rewrite)
 * =====================================================
 * Strips the legacy multi-vendor UI (Twilio SMS + Twilio WhatsApp + DLT
 * Portal uploads + AiSensy custom provider) and replaces it with two
 * elements per the Phase 291 spec:
 *
 *   1. Resend Email Configuration Card — optional per-academy override.
 *      Empty values fall back to Out-Play's global high-deliverability
 *      relay (Strategy A default).
 *   2. WhatsApp Status Banner — read-only success banner advertising the
 *      centralised Meta WhatsApp Business relay (zero per-tenant config).
 *
 * The 790-line predecessor file is gone — every Twilio/AiSensy/DLT field,
 * upload helper, and verification button has been removed. The `PUT
 * /settings` save payload now ships only `resend_api_key` +
 * `resend_from_email`, which the backend already accepts.
 */
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Mail, Save, CheckCircle2, Eye, EyeOff, Loader2, MessageCircle,
  ExternalLink, ShieldCheck, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const RESEND_GUIDE = [
  { step: 1, text: 'Create a free Resend account', link: 'https://resend.com/signup' },
  { step: 2, text: 'Generate an API key under API Keys', link: 'https://resend.com/api-keys' },
  { step: 3, text: 'Verify your sending domain for high deliverability', link: 'https://resend.com/domains' },
];

export default function IntegrationsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState(null);

  const [rs, setRs] = useState({ resend_api_key: '', resend_from_email: '' });
  const [showRs, setShowRs] = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const s = await api.get('/settings');
      setConfig(s.data);
      setRs({
        resend_api_key: '',
        resend_from_email: s.data.resend_from_email || '',
      });
    } catch (err) {
      toast.error('Failed to load communication settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (rs.resend_api_key) payload.resend_api_key = rs.resend_api_key;
      payload.resend_from_email = rs.resend_from_email || '';
      await api.put('/settings', payload);
      toast.success('Email settings saved');
      setRs((prev) => ({ ...prev, resend_api_key: '' }));
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const verifyResend = async () => {
    setTesting(true);
    try {
      const res = await api.post('/settings/integrations/resend/test', {
        resend_api_key: rs.resend_api_key,
        resend_from_email: rs.resend_from_email,
      });
      if (res.data?.success) toast.success(res.data.message || 'Resend verified');
      else toast.error(res.data?.message || 'Verification failed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-48 rounded-xl" />
        <Skeleton className="h-32 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="communication-settings">
      <header className="space-y-1">
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Communication Settings
        </h2>
        <p className="text-sm text-slate-500">
          Choose how Out-Play sends emails and WhatsApp messages on behalf of your academy.
        </p>
      </header>

      {/* ── WhatsApp Status Banner (centralised global relay) ─────────────── */}
      <Card
        className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50
                   dark:from-emerald-950/30 dark:to-teal-950/30"
        data-testid="whatsapp-relay-banner"
      >
        <CardContent className="p-5 flex items-start gap-4">
          <div className="shrink-0 h-11 w-11 rounded-full bg-emerald-600 flex items-center justify-center text-white">
            <MessageCircle className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <h3 className="text-base font-bold text-emerald-900 dark:text-emerald-100">
                WhatsApp Messaging Active
              </h3>
              <Badge
                className="bg-emerald-600 hover:bg-emerald-600 text-white text-[10px] uppercase tracking-wider"
                data-testid="whatsapp-relay-status"
              >
                <CheckCircle2 className="h-3 w-3 mr-1" /> Live · Meta Cloud
              </Badge>
            </div>
            <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
              All transactional alerts, fee receipts, payroll slips, and credential
              shares are automatically sent using Out-Play's high-speed Meta WhatsApp
              Business account &mdash; <strong>at no extra cost</strong>. No setup,
              no monthly fees, no per-message charges.
            </p>
            <div className="mt-3 flex items-center gap-2 text-[11px] text-emerald-700 dark:text-emerald-300">
              <ShieldCheck className="h-3.5 w-3.5" />
              <span>Pre-approved templates &middot; 99.9% delivery &middot; 1-2 second latency</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Resend Email Configuration Card ───────────────────────────────── */}
      <Card data-testid="resend-config-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-blue-600" />
                Resend Email Configuration
              </CardTitle>
              <CardDescription>
                Optional &mdash; connect your own Resend account to send from your
                academy's domain.
              </CardDescription>
            </div>
            {config?.resend_from_email ? (
              <Badge className="bg-blue-600 hover:bg-blue-600 text-white" data-testid="resend-status-connected">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Custom domain active
              </Badge>
            ) : (
              <Badge variant="outline" data-testid="resend-status-default">
                <Sparkles className="h-3 w-3 mr-1" /> Using Out-Play default
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-5">
          <div className="rounded-lg border border-blue-100 bg-blue-50/60 dark:bg-blue-950/20 dark:border-blue-900 px-4 py-3">
            <p className="text-sm text-blue-900 dark:text-blue-200 leading-relaxed">
              <strong>Leave blank to automatically use Out-Play's global
              high-deliverability email server.</strong> Add your own Resend API key only
              if you want emails sent from your academy's domain.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="resend-api-key" className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Resend API Key
              </Label>
              <div className="relative">
                <Input
                  id="resend-api-key"
                  type={showRs ? 'text' : 'password'}
                  placeholder={config?.resend_api_key_set ? '••••••••••• (saved)' : 're_xxxxxxxxxx'}
                  value={rs.resend_api_key}
                  onChange={(e) => setRs((p) => ({ ...p, resend_api_key: e.target.value }))}
                  className="pr-10"
                  data-testid="resend-api-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowRs(!showRs)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="resend-key-toggle"
                  aria-label={showRs ? 'Hide API key' : 'Show API key'}
                >
                  {showRs ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="resend-from-email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                From Email Domain
              </Label>
              <Input
                id="resend-from-email"
                type="email"
                placeholder="noreply@your-academy.in"
                value={rs.resend_from_email}
                onChange={(e) => setRs((p) => ({ ...p, resend_from_email: e.target.value }))}
                data-testid="resend-from-email-input"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap pt-1">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="comm-settings-save-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Email Settings
            </Button>
            <Button
              variant="outline"
              onClick={verifyResend}
              disabled={testing || !rs.resend_api_key}
              data-testid="resend-verify-btn"
            >
              {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Verify Resend
            </Button>
          </div>

          {/* Inline guide */}
          <details className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/30">
            <summary className="cursor-pointer px-4 py-2.5 text-xs font-semibold text-slate-600 select-none">
              How to get a Resend API key &amp; verify your domain
            </summary>
            <ol className="space-y-1.5 px-4 pb-4 pt-1 text-sm">
              {RESEND_GUIDE.map((g) => (
                <li key={g.step} className="flex items-start gap-2 text-slate-700 dark:text-slate-300">
                  <span className="shrink-0 h-5 w-5 rounded-full bg-blue-100 text-blue-700 text-[11px] font-bold flex items-center justify-center mt-0.5">
                    {g.step}
                  </span>
                  <span>
                    {g.text}{' '}
                    <a href={g.link} target="_blank" rel="noopener noreferrer"
                       className="text-blue-600 hover:underline inline-flex items-center gap-0.5">
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </span>
                </li>
              ))}
            </ol>
          </details>
        </CardContent>
      </Card>
    </div>
  );
}
